import { LeftOutlined } from '@ant-design/icons'
import { FC, ReactNode, useMemo, useEffect } from 'react'
import styled, { createGlobalStyle, css } from 'styled-components'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'

import { StepIndicator } from './StepIndicator'
import { MenuButton } from './MenuButton'
import UserFeedback from '@/components/common/UserFeedback'

const HEADER_HEIGHT = 70

const GlobalStyle = createGlobalStyle`
  :root body {
    overflow-x: auto;
  }
`

type ToolsLayoutProps = {
  step?: {
    /**
     * starts from 0
     */
    currentStep: number
    steps: {
      title: string
    }[]
  }
  fillMainHeight?: boolean
  mainPadding?: number | string
  children?: ReactNode
  className?: string
  goto?: {
    href: string;
    text: string;
  } | null;
  onRouterChange?: {
    start?: () => void;
    complete?: () => void;
  }
}

export const CustomMenuToolsLayout: FC<
  ToolsLayoutProps & {
    menu?: ReactNode
  }
> = ({
  step,
  menu,
  fillMainHeight,
  mainPadding,
  children,
  className,
  goto,
}) => {
  const { t } = useTranslation('common')

  const { currentStep, steps } = step || {}

  return (
    <ToolsLayoutContainer className={className}>
      <Header>
        <GoToDashboardButtonWrapper>
          <GoToDashboardButton href={goto?.href ? goto.href : "/dashboard"}>
            <LeftOutlined />
            <div>{goto?.text ? goto.text : t('Dashboard')}</div>
          </GoToDashboardButton>
        </GoToDashboardButtonWrapper>

        <StepIndicatorWrapper>
          <StepIndicator currentStep={currentStep} steps={steps} />
        </StepIndicatorWrapper>

        {menu}
      </Header>

      <Main
        $fillHeight={fillMainHeight}
        $padding={mainPadding}
      >
        {children}
      </Main>

      <GlobalStyle />
    </ToolsLayoutContainer>
  )
}

export const ToolsLayout: FC<ToolsLayoutProps> = ({
  step,
  fillMainHeight,
  mainPadding,
  children,
  className,
  goto,
  onRouterChange,
}) => {
  const session = useSession()
  const router = useRouter()

  const isLoggedIn = !!session.data?.user?.id

  /**
   * 경로에서 첫 / 를 제거한 이름을 피드백에 전달
   */
  const name = useMemo(() => {
    return router.pathname.startsWith('/')
      ? router.pathname.slice(1)
      : router.pathname
  }, [router.pathname])

  useEffect(() => {
    if (onRouterChange?.start) {
      router.events.on("routeChangeStart", onRouterChange.start);
    }
    if (onRouterChange?.complete) {
      router.events.on("routeChangeComplete", onRouterChange.complete);
      router.events.on("routeChangeError", onRouterChange.complete);
    }
    
    return () => {
      if (onRouterChange?.start) {
        router.events.off("routeChangeStart", onRouterChange.start);
      }
      if (onRouterChange?.complete) {
        router.events.off("routeChangeComplete", onRouterChange.complete);
        router.events.off("routeChangeError", onRouterChange.complete);
      }
    }
  }, [onRouterChange, router.events]);

  return (
    <CustomMenuToolsLayout
      step={step}
      className={className}
      menu={
        <MenuButtonWrapper>
          {/*
            로그인 되어 있으면 피드백 버튼으로 표시\
            아니면 메뉴 버튼
          */}
          {isLoggedIn ? <UserFeedback name={name} /> : <MenuButton />}
        </MenuButtonWrapper>
      }
      fillMainHeight={fillMainHeight}
      mainPadding={mainPadding}
      goto={goto}
    >
      {children}
    </CustomMenuToolsLayout>
  )
}

const ToolsLayoutContainer = styled.div`
  font-family: 'Noto Sans CJK';
`

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;

  position: fixed;
  top: 0;
  width: 100%;
  z-index: 2;

  background-color: white;
  height: ${HEADER_HEIGHT}px;
  padding: 0 40px;
  box-sizing: border-box;

  box-shadow: 0px 4px 4px 0px #00000040;

  @media (max-width: ${({ theme }) => theme.screenMD}px) {
    padding: 0;
  }
`

const GoToDashboardButtonWrapper = styled.div`
  flex: 0 350px;

  display: flex;

  @media (max-width: ${({ theme }) => theme.screenLG}px) {
    div {
      display: none;
    }
  }
`

const GoToDashboardButton = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 22px;

  color: #53677b;
  font-size: 14px;
  line-height: 20px;
  text-decoration: none;
  border-radius: 8px;
  padding: 8px 24px;
  transition: background-color 0.3s;

  &:hover {
    background-color: #f0f2f5;
  }
`

const MenuButtonWrapper = styled.div`
  flex: 0 350px;

  display: flex;
  justify-content: flex-end;
`

const StepIndicatorWrapper = styled.div`
  flex: 0 1 600px;
`

const Main = styled.main<{ $fillHeight?: boolean; $padding?: number | string }>`
  margin-top: ${HEADER_HEIGHT}px;
  padding: ${({ $padding }) => {
    if (typeof $padding === "number") {
      return `${$padding}px`;
    }
    if (typeof $padding === "string") {
      return $padding;
    }
    return "24px";
  }};

  ${({ $fillHeight }) =>
    $fillHeight &&
    css`
      box-sizing: border-box;
      height: calc(100vh - ${HEADER_HEIGHT}px);
      overflow: auto;
    `}

  @media (max-width: ${({ theme }) => theme.screenMD}px) {
    padding-left: 0;
    padding-right: 0;
  }
`
